// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import "swiper/css";
import "swiper/css/a11y";
import "swiper/css/autoplay";
import "swiper/css/controller";
import "swiper/css/effect-fade";
import "swiper/css/free-mode";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/scrollbar";
import { A11y, Autoplay, Controller, EffectFade, FreeMode, Navigation, Pagination, Scrollbar } from "swiper/modules";
import { Swiper, SwiperProps, SwiperSlide } from "swiper/react";

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import omitMultiple from "@insite/mobius/utilities/omitMultiple";
import pick from "lodash/pick";
import React, { FC, isValidElement, useLayoutEffect, useRef, useState } from "react";
import { mergeWithCss, xClassNames } from "@stoneage/XDL/Utilities";
import ArrowNavigation from "@stoneage/App/Components/XngCarousel/ArrowNavigation";
import { SwiperStyle } from "@stoneage/App/Components/XngCarousel/models";
import { stoneageStyles } from "@stoneage/App/Components/XngCarousel/styles";

export interface XngCarouselProps extends SwiperProps {
    extendedStyles?: SwiperStyle;
    /** Enable lazy loading for all slides (uses SwiperSlide's native lazy prop in v12) */
    lazy?: boolean;
    withShadow?: boolean;
    shadowWidth?: string;
    shadowColor?: string;
    persistShadow?: boolean;
    slideMaxWidth?: string;
    arrowsOnMobile?: boolean;
    arrowPaginationOffset?: number;
    /** Renders prev/next arrows in the pagination bar below the slides */
    arrowsBelowSlides?: boolean;
    arrowClassNames?: {
        prev?: string;
        next?: string;
    };
}

const SWIPER_SLIDE_PROP_KEYS = ["tag", "zoom", "virtualIndex", "lazy"] as const;

const XngCarousel: FC<XngCarouselProps> = props => {
    const {
        children,
        className,
        extendedStyles,
        lazy = true,
        withShadow = true,
        shadowWidth = "0px",
        shadowColor = "common.ui1",
        persistShadow = false,
        slideMaxWidth,
        arrowsOnMobile = false,
        arrowsBelowSlides = false,
        slidesPerView = "auto",
        arrowClassNames,
        arrowPaginationOffset: arrowPaginationOffsetProps,
        ...swiperProps
    } = props;

    const [styles] = useState(() => mergeWithCss(stoneageStyles, extendedStyles));

    const [prevDisabled, setPrevDisabled] = useState(true);
    const [nextDisabled, setNextDisabled] = useState(false);

    const paginationRef = useRef<HTMLDivElement>(null);
    const [paginationHeight, setPaginationHeight] = useState(0);

    useLayoutEffect(() => {
        const el = paginationRef.current;
        if (!el) {
            return;
        }

        const ro = new ResizeObserver(([entry]) => {
            requestAnimationFrame(() => {
                setPaginationHeight((entry.contentRect.height || 0) + 16);
            });
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    const paginationOptions = swiperProps.pagination
        ? typeof swiperProps.pagination === "boolean"
            ? { el: ".swiper-pagination", clickable: true }
            : swiperProps.pagination
        : undefined;

    const scrollbarOptions = swiperProps.scrollbar
        ? typeof swiperProps.scrollbar === "boolean"
            ? { draggable: true, el: ".swiper-scrollbar" }
            : swiperProps.scrollbar
        : undefined;

    const isVertical = swiperProps.direction === "vertical";
    const hasPagination = !!(swiperProps.pagination || (swiperProps.scrollbar && !isVertical) || arrowsBelowSlides);
    const arrowPaginationOffset = hasPagination ? (arrowPaginationOffsetProps ?? paginationHeight) : 0;
    const showNavigation = !!swiperProps.navigation || arrowsBelowSlides;
    const isAutoWidth = slidesPerView === "auto" && !props.breakpoints && !isVertical;

    return (
        <StyledWrapper
            {...styles.container}
            className={xClassNames.append(className, "carousel-main-wrapper")}
            {...{
                isAutoWidth,
                withShadow,
                shadowWidth,
                prevDisabled,
                nextDisabled,
                persistShadow,
                shadowColor,
                slideMaxWidth,
                direction: swiperProps.direction,
                height: swiperProps.height,
                arrowsBelowSlides,
            }}
        >
            <Swiper
                {...swiperProps}
                modules={[Navigation, Pagination, Scrollbar, A11y, Controller, Autoplay, EffectFade, FreeMode]}
                slidesPerView={slidesPerView}
                pagination={paginationOptions}
                scrollbar={scrollbarOptions}
                navigation={showNavigation}
                observeParents
                observer
            >
                {React.Children.map(children, child => {
                    if (!isValidElement(child)) {
                        return null;
                    }

                    const slideProps = pick(child.props, SWIPER_SLIDE_PROP_KEYS);
                    const childProps = omitMultiple(child.props as any, [...SWIPER_SLIDE_PROP_KEYS]);

                    return (
                        <SwiperSlide {...slideProps} data-test-selector="xngCarousel_swiperSlide">
                            {React.createElement(child.type, childProps)}
                            {lazy && <div className="swiper-lazy-preloader"></div>}
                        </SwiperSlide>
                    );
                })}

                {!arrowsBelowSlides && (
                    <>
                        <ArrowNavigation
                            arrowType="prev"
                            showOnMobile={arrowsOnMobile}
                            showNavigation={showNavigation}
                            paginationOffset={arrowPaginationOffset}
                            styleOverrides={styles.prevArrow}
                            onDisabled={setPrevDisabled}
                            className={arrowClassNames?.prev}
                        />
                        <ArrowNavigation
                            arrowType="next"
                            showOnMobile={arrowsOnMobile}
                            showNavigation={showNavigation}
                            paginationOffset={arrowPaginationOffset}
                            styleOverrides={styles.nextArrow}
                            onDisabled={setNextDisabled}
                            className={arrowClassNames?.next}
                        />
                    </>
                )}

                {hasPagination && (
                    <StyledWrapper className="xng-pagination-container" ref={paginationRef}>
                        <StyledWrapper className="xng-pagination-inner-container">
                            {arrowsBelowSlides && (
                                <ArrowNavigation
                                    arrowType="prev"
                                    showOnMobile
                                    showNavigation={true}
                                    styleOverrides={{ src: "ChevronLeft", size: 14, color: "common.ui7" }}
                                    onDisabled={setPrevDisabled}
                                    className="arrow-below-slides"
                                />
                            )}
                            {swiperProps.pagination && <div className="swiper-pagination" />}
                            {arrowsBelowSlides && (
                                <ArrowNavigation
                                    arrowType="next"
                                    showOnMobile
                                    showNavigation={true}
                                    styleOverrides={{ src: "ChevronRight", size: 14, color: "common.ui7" }}
                                    onDisabled={setNextDisabled}
                                    className="arrow-below-slides"
                                />
                            )}
                        </StyledWrapper>
                        {swiperProps.scrollbar && !isVertical && <div className="swiper-scrollbar" />}
                    </StyledWrapper>
                )}

                {swiperProps.scrollbar && isVertical && <div className="swiper-scrollbar" />}
            </Swiper>
        </StyledWrapper>
    );
};

export default XngCarousel;
